import { useEffect } from 'react';
import { subscribe } from 'lib/pusher';
import { useLatest } from './use-latest';

type ChannelEventHandler = (data: never) => void;
type ChannelHandlers = Record<string, ChannelEventHandler>;

/** The part of a pusher-js `Channel` this hook touches. */
interface DocumentChannel {
  bind: (event: string, handler: ChannelEventHandler) => void;
  unbind: (event: string, handler: ChannelEventHandler) => void;
}

/**
 * Binds named handlers on a document's Pusher channel. The channel object is
 * shared by every component on the page, so each handler is unbound by
 * reference (an unbind by event name would strip the other components' too)
 * and the channel stays subscribed. Handlers may change identity every render;
 * the bound wrappers always call the latest one.
 * @param {string | undefined} channelName - The document's channel; nothing binds without one
 * @param {ChannelHandlers} handlers - The handler for each event name
 */
export const useDocumentChannelEvents = (
  channelName: string | undefined,
  handlers: ChannelHandlers
) => {
  const handlersRef = useLatest(handlers);
  const eventNames = Object.keys(handlers).sort().join(',');

  useEffect(() => {
    if (!channelName || !eventNames) return undefined;
    const channel = subscribe(channelName) as DocumentChannel;
    const bound = eventNames.split(',').map((name) => ({
      name,
      fn: (data: never) => handlersRef.current[name]?.(data),
    }));
    bound.forEach(({ name, fn }) => channel.bind(name, fn));
    return () => bound.forEach(({ name, fn }) => channel.unbind(name, fn));
  }, [channelName, eventNames, handlersRef]);
};
